import type { QueryClient } from "@tanstack/react-query";
import { api, type Me } from "../api/client";

// The conversation list's key (api/chat's chatKeys.conversations; not imported: api/hooks
// imports this module, and api/chat imports api/hooks).
const CONVERSATIONS = ["conversations"] as const;

// P2-A8 (Redmine #3137): the installed app always started on New chat (iOS and Android end it
// in the background; the manifest's start_url is `/`). Each device remembers the last place —
// the address's path and search, a message link's `m` left out — and the app, started at bare
// `/`, opens there instead. A link or a notification opens as asked (it isn't bare `/`).

const KEY = "vx.last-place";
const NEVER = /^\/(?:signin|auth)(?:\/|$)/;

function read(): string | null {
  try {
    return window.localStorage.getItem(KEY);
  } catch {
    return null;
  }
}

export function forgetPlace() {
  try {
    window.localStorage.removeItem(KEY);
  } catch {
    // storage blocked: nothing was kept
  }
}

/** The place an address stands for: its path and search, without `m`. */
export function placeOf(pathname: string, search: string): string {
  const params = new URLSearchParams(search);
  params.delete("m");
  const rest = params.toString();
  return rest ? `${pathname}?${rest}` : pathname;
}

export function rememberPlace(pathname: string, search: string) {
  if (NEVER.test(pathname)) return;
  try {
    // New chat is New chat: a question it was opened with (`?q=…`) isn't kept (review round 1).
    window.localStorage.setItem(KEY, pathname === "/" ? "/" : placeOf(pathname, search));
  } catch {
    // storage blocked (a private window): the app starts on New chat, as before
  }
}

/** Where a start at bare `/` goes: the stored place when it's still there and reachable, else
 *  null (New chat), forgetting one that isn't. Checked before the first screen shows. */
export async function placeToRestore(qc: QueryClient, me: Me): Promise<string | null> {
  const place = read();
  if (!place || place === "/" || NEVER.test(place)) return null;
  const path = place.split("?")[0] ?? "";
  if (await reachable(qc, me, path)) return place;
  forgetPlace();
  return null;
}

async function reachable(qc: QueryClient, me: Me, path: string): Promise<boolean> {
  if (path.startsWith("/admin")) return me.role === "admin";
  const chat = /^\/c\/([^/]+)$/.exec(path);
  if (chat) {
    const r = await api
      .GET("/api/conversations/{conversation_id}", {
        params: { path: { conversation_id: decodeURIComponent(chat[1] ?? "") } },
      })
      .catch(() => null);
    return Boolean(r?.data);
  }
  const channel = /^\/ch\/([^/]+)$/.exec(path);
  if (channel) {
    const list = await qc
      .fetchQuery({
        queryKey: CONVERSATIONS,
        queryFn: async () => (await api.GET("/api/conversations")).data ?? [],
        staleTime: 15_000,
      })
      .catch(() => []);
    const slug = decodeURIComponent(channel[1] ?? "");
    return list.some((c) => c.kind === "channel" && c.slug === slug);
  }
  return /^\/(?:inbox|automations|me)$/.test(path);
}
